import {useQuery} from "@apollo/client";
import {useEffect, useMemo, useRef} from "react";

import {LoadingContainer} from "@/components/placeholder/LoadingContainer.tsx";
import {DashboardChartsData, GQL_DASHBOARD_CHARTS} from "@/lib/queries/website.ts";
import {DashboardChartInput} from "@/lib/types/website.ts";
import {DashboardChartCard} from "@/pages/dashboard/display/components/DashboardChartCard.tsx";

import styles from "./DashboardCharts.module.scss";

interface DashboardChartsProps {
  charts: DashboardChartInput[];
  /** The dashboard's auto-refresh interval in seconds; charts refresh with the rest of it. */
  refreshRate?: number;
}

const PENDING_RETRY_BASE_MS = 3000;
const PENDING_RETRY_JITTER_MS = 2000;
/** Past this, a pending chart waits for the dashboard's next refresh like everything else. */
const MAX_PENDING_RETRIES = 5;

/**
 * The dashboard's chart widgets.
 *
 * One query for every chart: the server serves them from a shared cache, so a dashboard left open
 * on auto-refresh costs a cache read per poll rather than a query per chart.
 */
export const DashboardCharts = ({charts, refreshRate}: DashboardChartsProps) => {
  // The saved placements carry an id, a name and a __typename the input does not accept.
  const variables = useMemo(() => ({charts: charts.map(({view_id, time_range}) => ({view_id, time_range}))}), [charts]);
  const {data, previousData, loading, refetch} = useQuery<DashboardChartsData>(GQL_DASHBOARD_CHARTS, {
    variables,
    skip: charts.length === 0,
    pollInterval: refreshRate ? refreshRate * 1000 : 0,
  });
  const retries = useRef(0);

  const widgets = (loading ? previousData : data)?.dashboardCharts;
  const hasPending = widgets?.some((widget) => widget.status === "PENDING") ?? false;

  useEffect(() => {
    if (!hasPending) {
      retries.current = 0;
      return;
    }
    if (retries.current >= MAX_PENDING_RETRIES) {
      return;
    }
    // Another request is computing these charts. Ask again shortly rather than at the next
    // refresh, jittered so dashboards opened together do not all retry at once.
    const timer = window.setTimeout(
      () => {
        retries.current += 1;
        refetch();
      },
      PENDING_RETRY_BASE_MS + Math.random() * PENDING_RETRY_JITTER_MS
    );
    return () => window.clearTimeout(timer);
  }, [widgets, hasPending, refetch]);

  if (charts.length === 0) {
    return null;
  }

  return (
    <div className={styles.section}>
      <div className="pulseTextH2Bold">Charts</div>
      {widgets ? (
        <div className={styles.grid}>
          {widgets.map((widget, index) => (
            <DashboardChartCard key={`${widget.view_id}-${widget.time_range}-${index}`} widget={widget} />
          ))}
        </div>
      ) : (
        <LoadingContainer text="Loading charts…" />
      )}
    </div>
  );
};
